import { useState } from 'react';
import { redirectToSubscriptionCheckout } from '@/lib/subscriptionCheckoutRedirect';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useCreateCheckoutSessionMutation } from '@/apis/extended/subscriptionApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  selectedLoanProgram?: USER_LOAN_PROGRAM;
  onUpgraded?: () => void;
}

export function PaywallModal({
  open,
  onClose,
  selectedLoanProgram,
  onUpgraded,
}: PaywallModalProps) {
  const [createCheckoutSession, { isLoading }] =
    useCreateCheckoutSessionMutation();
  const [isProcessing, setIsProcessing] = useState(false);

  const getProgramDisplayName = () => {
    switch (selectedLoanProgram) {
      case USER_LOAN_PROGRAM.BRIDGE:
        return 'Bridge Loans';
      case USER_LOAN_PROGRAM.DSCR:
        return 'DSCR Loans';
      case USER_LOAN_PROGRAM.COMMERCIAL:
        return 'Commercial Loans';
      case USER_LOAN_PROGRAM.FIX_AND_FLIP:
        return 'Fix and Flip Loans';
      default:
        return 'Investor Products';
    }
  };

  const handleUpgrade = async () => {
    try {
      setIsProcessing(true);

      const result = await createCheckoutSession({
        plan: 'investor_monthly',
        successUrl: `${window.location.origin}/profile/settings?subscription=pending`,
        cancelUrl: `${window.location.origin}/pricing?subscription=cancelled`,
      }).unwrap();

      const ok = await redirectToSubscriptionCheckout(result);
      if (!ok) setIsProcessing(false);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-500" />
            Investor Subscription Required
          </DialogTitle>
          <DialogDescription>
            {getProgramDisplayName()} are available to Investor subscribers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg border bg-muted/50 p-4">
            <h3 className="font-semibold mb-2">What you'll get:</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>{getProgramDisplayName()}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>
                  Access to all investor loan products (Bridge, DSCR,
                  Commercial, Fix & Flip)
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Advanced investment calculators</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Priority support</span>
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-primary/10 p-3">
            <div>
              <p className="text-sm font-medium">Investor Monthly</p>
              <p className="text-xs text-muted-foreground">$99/month</p>
            </div>
            <p className="text-lg font-bold">$99</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading || isProcessing}
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={isLoading || isProcessing}
            className="min-w-[120px]"
          >
            {isLoading || isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Upgrade Now'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
